import { Injectable, NgZone } from '@angular/core';
import { SettingService } from './setting.service';

declare var turnstile: any;

const CONTAINER_ID = 'cf-turnstile-container';
const EXECUTE_TIMEOUT_MS = 20000;

/**
 * Genera tokens de Cloudflare Turnstile en el navegador.
 * El token solo prueba que el request viene del navegador; la verificación
 * real se hace en el backend contra la API de Cloudflare con la llave secreta.
 *
 * A diferencia de reCAPTCHA v3 (que expone un execute() que genera un token
 * "invisible" sin widget), Turnstile necesita un widget renderizado en el DOM
 * (modo "Managed": normalmente invisible, solo pide interacción si Cloudflare
 * detecta algo sospechoso). Por eso este servicio crea un contenedor oculto
 * una sola vez y lo reutiliza para cada ejecución.
 */
@Injectable({
  providedIn: 'root'
})
export class TurnstileService {
  private scriptPromise: Promise<void> | null = null;
  private widgetId: string | null = null;
  private pendingResolve: ((token: string) => void) | null = null;
  private pendingReject: ((err: any) => void) | null = null;

  constructor(private settingsService: SettingService, private zone: NgZone) { }

  private loadScript(): Promise<void> {
    if (typeof turnstile !== 'undefined') {
      return Promise.resolve();
    }
    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    this.scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject('No se pudo cargar el script de Cloudflare Turnstile');
      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private ensureContainer(): HTMLElement {
    let el = document.getElementById(CONTAINER_ID);
    if (!el) {
      el = document.createElement('div');
      el.id = CONTAINER_ID;
      // El widget en modo "Managed" es invisible salvo que Cloudflare decida
      // mostrar un challenge; lo dejamos anclado abajo a la derecha por si acaso.
      el.style.position = 'fixed';
      el.style.bottom = '12px';
      el.style.right = '12px';
      el.style.zIndex = '99999';
      document.body.appendChild(el);
    }
    return el;
  }

  private resolvePending(token: string) {
    this.zone.run(() => {
      if (this.pendingResolve) {
        this.pendingResolve(token);
      }
      this.pendingResolve = null;
      this.pendingReject = null;
    });
  }

  private rejectPending(err: any) {
    this.zone.run(() => {
      if (this.pendingReject) {
        this.pendingReject(err);
      }
      this.pendingResolve = null;
      this.pendingReject = null;
    });
  }

  /**
   * Ejecuta Turnstile para una acción y devuelve el token temporal que debe
   * viajar en el request al backend (campo sCaptchaToken). Cada llamada
   * genera un token nuevo (los tokens son de un solo uso): si el widget ya
   * fue renderizado antes, se resetea y se vuelve a ejecutar.
   */
  async execute(action: string): Promise<string> {
    const siteKey = this.settingsService.settings.turnstileSiteKey;
    if (!siteKey) {
      throw new Error('turnstileSiteKey no configurada en settings.json');
    }

    await this.loadScript();
    const container = this.ensureContainer();

    const executionPromise = new Promise<string>((resolve, reject) => {
      this.pendingResolve = resolve;
      this.pendingReject = reject;

      // No usamos turnstile.ready(): requiere que el script se haya cargado
      // SIN async/defer. loadScript() ya garantiza (vía su propio onload)
      // que la API está disponible antes de llegar acá.
      // Con execution:'execute' el widget NUNCA se auto-ejecuta al renderizar:
      // siempre hay que llamar a execute() explícitamente, la primera vez y las siguientes.
      if (this.widgetId === null) {
        this.widgetId = turnstile.render(container, {
          sitekey: siteKey,
          action,
          execution: 'execute',
          callback: (token: string) => this.resolvePending(token),
          'error-callback': (err: any) => this.rejectPending(err),
          'expired-callback': () => this.rejectPending('token-expired'),
          'timeout-callback': () => this.rejectPending('timeout')
        });
      } else {
        turnstile.reset(this.widgetId);
      }

      turnstile.execute(this.widgetId, { action });
    });

    // Respaldo propio: si Cloudflare muestra un challenge visual (poco común,
    // solo cuando detecta algo sospechoso) y el usuario no llega a resolverlo
    // a tiempo, no dejamos el formulario colgado esperando para siempre.
    return this.withTimeout(executionPromise, EXECUTE_TIMEOUT_MS);
  }

  private withTimeout(promise: Promise<string>, ms: number): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingResolve = null;
        this.pendingReject = null;
        reject('client-timeout');
      }, ms);

      promise.then(
        (token) => { clearTimeout(timer); resolve(token); },
        (err) => { clearTimeout(timer); reject(err); }
      );
    });
  }
}
